import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SkillsVisualization = () => {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [animationStarted, setAnimationStarted] = useState(false);

  const skillCategories = [
    {
      category: "Frontend Development",
      icon: "Monitor",
      skills: [
        { name: "React", level: 95, experience: "5+ years", projects: 25, icon: "⚛️" },
        { name: "TypeScript", level: 90, experience: "4+ years", projects: 20, icon: "📘" },
        { name: "Next.js", level: 88, experience: "3+ years", projects: 15, icon: "▲" },
        { name: "Tailwind CSS", level: 92, experience: "3+ years", projects: 30, icon: "🎨" },
        { name: "Vue.js", level: 75, experience: "2+ years", projects: 8, icon: "💚" }
      ]
    },
    {
      category: "Backend Development",
      icon: "Server",
      skills: [
        { name: "Node.js", level: 93, experience: "5+ years", projects: 22, icon: "🟢" },
        { name: "Python", level: 85, experience: "4+ years", projects: 18, icon: "🐍" },
        { name: "Express.js", level: 90, experience: "4+ years", projects: 20, icon: "🚀" },
        { name: "PostgreSQL", level: 82, experience: "3+ years", projects: 15, icon: "🐘" },
        { name: "MongoDB", level: 88, experience: "4+ years", projects: 16, icon: "🍃" }
      ]
    },
    {
      category: "DevOps & Tools",
      icon: "Settings",
      skills: [
        { name: "Docker", level: 80, experience: "3+ years", projects: 12, icon: "🐳" },
        { name: "AWS", level: 75, experience: "2+ years", projects: 10, icon: "☁️" },
        { name: "Git", level: 95, experience: "5+ years", projects: 50, icon: "📝" },
        { name: "CI/CD", level: 78, experience: "2+ years", projects: 8, icon: "🔄" },
        { name: "Kubernetes", level: 65, experience: "1+ years", projects: 5, icon: "⚙️" }
      ]
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationStarted(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getSkillColor = (level) => {
    if (level >= 90) return 'bg-success';
    if (level >= 80) return 'bg-accent';
    if (level >= 70) return 'bg-warning';
    return 'bg-text-secondary';
  };

  return (
    <section className="py-20 bg-muted">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Code2" size={20} className="text-accent" />
            <span className="text-sm font-medium text-text-secondary tracking-wide uppercase">
              Technical Expertise
            </span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
            Skills & Technologies
          </h2>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            A comprehensive overview of my technical skills, experience levels, 
            and practical application across various projects and technologies.
          </p>
        </div>

        {/* Skills Categories */}
        <div className="space-y-12">
          {skillCategories?.map((category, categoryIndex) => (
            <div
              key={category?.category}
              className="animate-fade-in-up"
              style={{ animationDelay: `${categoryIndex * 200}ms` }}
            >
              {/* Category Header */}
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                  <Icon name={category?.icon} size={24} className="text-accent-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary">{category?.category}</h3>
                  <p className="text-sm text-text-secondary">
                    {category?.skills?.length} technologies
                  </p>
                </div>
              </div>

              {/* Skills Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category?.skills?.map((skill, skillIndex) => (
                  <div
                    key={skill?.name}
                    className="bg-card rounded-xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 cursor-pointer group"
                    onMouseEnter={() => setHoveredSkill(`${category?.category}-${skill?.name}`)}
                    onMouseLeave={() => setHoveredSkill(null)}
                  >
                    {/* Skill Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{skill?.icon}</span>
                        <div>
                          <h4 className="font-semibold text-primary group-hover:text-accent transition-colors duration-300">
                            {skill?.name}
                          </h4>
                          <p className="text-xs text-text-secondary">{skill?.experience}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-primary">{skill?.level}%</div>
                        <div className="text-xs text-text-secondary">{skill?.projects} projects</div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="relative">
                      <div className="w-full bg-border rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-1000 ease-out ${getSkillColor(skill?.level)}`}
                          style={{
                            width: animationStarted ? `${skill?.level}%` : '0%',
                            transitionDelay: `${(categoryIndex * 200) + (skillIndex * 100)}ms`
                          }}
                        ></div>
                      </div>
                      
                      {/* Hover Details */}
                      {hoveredSkill === `${category?.category}-${skill?.name}` && (
                        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-lg text-xs whitespace-nowrap animate-scale-in">
                          {skill?.projects} projects • {skill?.experience}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-primary"></div>
                        </div>
                      )}
                    </div>

                    {/* Skill Level Indicator */}
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-text-secondary">Proficiency</span>
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4, 5]?.map((dot) => (
                          <div
                            key={dot}
                            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                              dot <= Math.ceil(skill?.level / 20) 
                                ? getSkillColor(skill?.level)?.replace('bg-', 'bg-')
                                : 'bg-border'
                            }`}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Skills Summary */}
        <div className="mt-16 bg-card rounded-xl p-8 shadow-soft animate-fade-in-up" style={{ animationDelay: '600ms' }}>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-accent mb-2">15+</div>
              <div className="text-sm text-text-secondary">Technologies</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-success mb-2">5+</div>
              <div className="text-sm text-text-secondary">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-warning mb-2">50+</div>
              <div className="text-sm text-text-secondary">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-2">3</div>
              <div className="text-sm text-text-secondary">Specializations</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsVisualization;